/**
 * Meddelande till hyresgäst enligt 12 kap. 18 e § jordabalken (Formulär R-06).
 *
 * The landlord's advance notice of planned ombyggnads- och ändringsåtgärder.
 * Signed by the landlord alone and sent by rekommenderad post — never
 * routed to an e-signing provider. The 18 d Godkännande travels with it.
 *
 * Legal text verbatim from `base-templates/18 d godkännande och 18e
 * meddelande.docx` (Haymanot-approved).
 */
import { em, group, nodes, p, section } from '@hyranai/regta-document-ir';
import { fieldRows, numVal, optionVal, partyDetailsBlock, partySlot, propertySlot, selectSlot, signingNodes, textVal, } from '../common/index.js';
import { ROOM_OPTIONS, legalMeta } from './kit.js';
export const LEGAL_18 = legalMeta('base-templates/18 d godkännande och 18e meddelande.docx');
/* The landlord / hyresobjekt / tenant panels are shared verbatim with the 18 d Godkännande. */
export const LANDLORD_FIELDS = [
    { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
    { key: 'landlord_org_number', label: 'Personnr / Org.nr', field: 'legalId', required: true },
    { key: 'landlord_address', label: 'Adress', field: 'address', required: true },
];
export const TENANT_FIELDS = [
    { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
    { key: 'tenant_personnummer', label: 'Personnr / Org.nr', field: 'legalId', help: 'PII — ej sökbart' },
];
export const TENANT_CONTACT_FIELDS = [
    { key: 'tenant_email', label: 'E-post', field: 'email' },
    { key: 'tenant_phone', label: 'Telefon', field: 'phone', printed: 'Telefonnummer' },
];
export const HYRESOBJEKT_SLOTS = [
    {
        key: 'apartment_official_number',
        label: 'Lägenhetsnummer',
        type: 'text',
        help: 'T.ex. det officiella lägenhetsnumret (1101 etc.)',
    },
    selectSlot('apartment_rooms', 'Antal rum och kök', ROOM_OPTIONS),
    { key: 'apartment_area_sqm', label: 'Yta i m²', type: 'number', min: 0 },
    propertySlot('property_address', 'Adress (postnummer och kommun)', 'address', { type: 'textarea', required: true }),
    propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true }),
];
export const hyresobjektRows = (input, ctx) => nodes(fieldRows([
    ['Lägenhetsnummer', [textVal(input, 'apartment_official_number')]],
    ['Antal rum och kök', [optionVal(ROOM_OPTIONS, input, 'apartment_rooms')]],
    ['Yta i m²', [numVal(ctx, input, 'apartment_area_sqm')]],
    ['Adress (postnummer och kommun)', [textVal(input, 'property_address', 'long')]],
    ['Fastighetsbeteckning', [textVal(input, 'property_designation')]],
]));
export const hyresvard = {
    ...partyDetailsBlock({
        blockKey: 'meddelande_18e.hyresvard',
        ref: 'hyresvard',
        title: 'Uppgifter om hyresvärden',
        summary: 'The landlord’s name, id number and address.',
        role: 'lessor',
        ordinal: 0,
        fields: LANDLORD_FIELDS,
    }),
    ...LEGAL_18,
};
export const hyresobjekt = {
    ...LEGAL_18,
    blockKey: 'meddelande_18e.hyresobjekt',
    version: 'v1',
    title: 'Uppgifter om hyresobjekt',
    summary: 'The apartment: number, rooms, area, address and fastighetsbeteckning.',
    slots: HYRESOBJEKT_SLOTS,
    compile: (input, ctx) => [section('hyresobjekt', 'Uppgifter om hyresobjekt', hyresobjektRows(input, ctx))],
};
export const hyresgast = {
    ...partyDetailsBlock({
        blockKey: 'meddelande_18e.hyresgast',
        ref: 'hyresgast',
        title: 'Uppgifter om hyresgästen',
        summary: 'The recipient tenant’s name, id number and contact details.',
        role: 'lessee',
        ordinal: 0,
        fields: [...TENANT_FIELDS, ...TENANT_CONTACT_FIELDS],
    }),
    ...LEGAL_18,
};
export const information = {
    ...LEGAL_18,
    blockKey: 'meddelande_18e.information',
    version: 'v1',
    title: 'Information om planerade åtgärder',
    summary: 'Asks the tenant to approve the works by signing the enclosed 18 d form; otherwise hyresnämnden may permit them.',
    slots: [],
    compile: () => [
        section('information', 'Information om planerade åtgärder', [
            p('Din hyresvärd önskar genomföra de åtgärder som framgår i bifogad godkännandeblankett. Vi ber dig godkänna att åtgärderna får genomföras. Det sker genom att underteckna godkännandet nedan. Om vi inte erhåller godkännande kan hyresnämnden komma att ge tillstånd till åtgärderna.'),
        ]),
    ],
};
const LANDLORD_SIGNS = [{ label: 'Hyresvärdens namnteckning', role: 'lessor', ordinal: 0 }];
export const underskrift = {
    ...LEGAL_18,
    blockKey: 'meddelande_18e.underskrift',
    version: 'v1',
    title: 'Underskrift',
    summary: 'The landlord’s signature, its contact details, and the registered-post delivery note.',
    slots: [
        partySlot('landlord_phone', 'Telefon', 'lessor', 0, 'phone'),
        partySlot('landlord_email', 'E-post', 'lessor', 0, 'email'),
    ],
    compile: (input, ctx) => [
        group(nodes(...signingNodes({ ...input, parties: LANDLORD_SIGNS }, ctx), fieldRows([
            ['Telefonnummer', [textVal(input, 'landlord_phone')]],
            ['E-post', [textVal(input, 'landlord_email')]],
        ]), p([em('Skickad till hyresgäst med rekommenderad post.')], { variant: 'fine_print' })), { keepTogether: true }),
    ],
};
export const MEDDELANDE_18E_BLOCKS = [hyresvard, hyresobjekt, hyresgast, information, underskrift];
//# sourceMappingURL=blocks.js.map